/** @mui/material imports */
/** @mui/icons-material imports */
/** React imports */
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
/** Goldspot imports */
import {
  ColumnHeader,
  DatasetStorageType,
  JsonDataframe,
} from '../../TypeScript/types';
import { DatasetMetadataType, SignalMap } from '../../TypeScript/interfaces';
import {
  DatasetStorage,
  DEBOUNCED_REMOTE_UPDATE_TIME,
} from '../../TypeScript/constants';
import { appContext } from '../../App';
import { getPathPrefix } from './getPathPrefix';
import { uploadFileToS3 } from '../../utils/upload-file-api';

interface UseMetadataDeps {
  dataset: JsonDataframe;
  datasetType: DatasetStorageType;
  depthCol: ColumnHeader;
  depthMinCol: ColumnHeader;
  depthMaxCol: ColumnHeader;
  bhidCol: ColumnHeader;
  setBhidCol: (bhid_col: string) => void;
  setDepthCol: (bhid_col: string) => void;
  setDepthMinCol: (bhid_col: string) => void;
  setDepthMaxCol: (bhid_col: string) => void;
}

export const emptyMetadata: DatasetMetadataType = {
  columnHeaders: [],
  columnCount: 0,
  rowCount: 0,
  dataset: {},
  file: {
    name: '',
    type: 'csv',
    lastModified: 0,
    size: 0,
  },
};

export const useMetadata = ({
  dataset,
  datasetType,
  depthCol,
  depthMaxCol,
  depthMinCol,
  bhidCol,
  setBhidCol,
  setDepthCol,
  setDepthMinCol,
  setDepthMaxCol,
}: UseMetadataDeps) => {
  const [metadata, setMetadata] = useState<DatasetMetadataType>(emptyMetadata);
  const { activeProject, user } = useContext(appContext);
  const metadataRef = useRef<DatasetMetadataType>(emptyMetadata);
  const debouncedUpdateRemoteMetadataRef = useRef<NodeJS.Timeout>();

  const unloadMetadata = () => {
    metadataRef.current = emptyMetadata;
    setMetadata(emptyMetadata);
  };

  const getMetadata = () => {
    return metadataRef.current;
  };

  const getRemoteMetadataPath = (
    metadata: DatasetMetadataType,
    datasetType: DatasetStorageType,
    project: string,
    company: string,
  ): string | null => {
    if (metadata.rowCount > 0 && datasetType !== DatasetStorage.local) {
      const directory: string =
        datasetType === DatasetStorage.log
          ? metadata.log?.id || ''
          : datasetType === DatasetStorage.cloud
            ? metadata.file?.name || ''
            : '';
      const pathPrefix: string = getPathPrefix(directory, company, project);
      return pathPrefix + '/metadata.json';
    } else {
      return null;
    }
  };

  const remoteMetadataPath = useMemo(() => {
    return getRemoteMetadataPath(
      metadata,
      datasetType,
      activeProject.id || '',
      activeProject.company || '',
    );
  }, [activeProject, metadata, datasetType]);

  const isRatioColumnHeader = (columnHeader: string): boolean => {
    return /^ratio_/.test(columnHeader);
  };

  const alterSignalsMap = (
    mappingToAdd: Record<ColumnHeader, SignalMap>,
    mappingToRemove: Record<ColumnHeader, SignalMap> = {},
  ): void => {
    const columnHeaders: ColumnHeader[] = Object.keys(mappingToAdd);
    const updatedSignalsMap: Record<ColumnHeader, SignalMap> = {};
    const updatedColumnHeaders = metadata.columnHeaders || [];
    if (metadata.signals !== undefined) {
      Object.keys(metadata.signals).forEach((columnHeader: ColumnHeader) => {
        if (
          !mappingToRemove[columnHeader] &&
          metadata.signals !== undefined &&
          metadata.signals[columnHeader]
        ) {
          updatedSignalsMap[columnHeader] = Object.assign(
            {},
            metadata.signals[columnHeader],
          );
        } else if (
          isRatioColumnHeader(columnHeader) &&
          mappingToRemove[columnHeader]
        ) {
          // if removing a ratio signal, remove the ratio column header too
          if (updatedColumnHeaders.indexOf(columnHeader) !== -1) {
            const index: number = updatedColumnHeaders.indexOf(columnHeader);
            updatedColumnHeaders.splice(index, 1);
          }
        }
      });
    }
    columnHeaders.forEach((columnHeader: ColumnHeader): void => {
      updatedSignalsMap[columnHeader] = Object.assign(
        {},
        mappingToAdd[columnHeader],
      );
      if (updatedColumnHeaders.indexOf(columnHeader) === -1) {
        updatedColumnHeaders.push(columnHeader);
      }
    });
    const updatedMetadata: DatasetMetadataType = {
      ...metadata,
      signals: updatedSignalsMap,
      columnHeaders: updatedColumnHeaders,
    };
    applyUpdate(updatedMetadata);
  };

  const applyUpdate = (
    updatedMetadata: DatasetMetadataType,
    noSync = false,
  ): void => {
    metadataRef.current = updatedMetadata;
    setMetadata(updatedMetadata);
    if (!noSync) {
      updateRemoteMetadata(updatedMetadata);
    }
  };

  /**
   * Update the metadata in the cloud storage
   * @param metadata
   */
  const updateRemoteMetadata = (metadata: DatasetMetadataType): void => {
    const metadataPath = getRemoteMetadataPath(
      metadata,
      datasetType,
      activeProject.id || '',
      activeProject.company || '',
    );
    if (metadataPath) {
      const metadataFile = new File(
        [JSON.stringify(metadata)],
        'metadata.json',
        {
          type: 'application/json',
        },
      );
      if (debouncedUpdateRemoteMetadataRef.current) {
        clearTimeout(debouncedUpdateRemoteMetadataRef.current);
      }
      if (metadataPath) {
        debouncedUpdateRemoteMetadataRef.current = setTimeout(() => {
          uploadFileToS3(metadataFile, metadataPath, user);
        }, DEBOUNCED_REMOTE_UPDATE_TIME);
      }
    }
  };

  /**
   * sync tessellation form headers to metadata
   */
  useEffect(() => {
    if (metadata.rowCount > 0) {
      const updatedMappings: Record<string, ColumnHeader> = Object.assign(
        {},
        metadata.dataset,
      );
      let changed: boolean = false;
      if (bhidCol && metadata.dataset?.bhidCol !== bhidCol) {
        changed = true;
        updatedMappings.bhidCol = bhidCol;
      }
      if (depthCol && metadata.dataset?.depthCol !== depthCol) {
        changed = true;
        updatedMappings.depthCol = depthCol;
      }
      if (depthMinCol && metadata.dataset?.depthMinCol !== depthMinCol) {
        changed = true;
        updatedMappings.depthMinCol = depthMinCol;
      }
      if (depthMaxCol && metadata.dataset?.depthMaxCol !== depthMaxCol) {
        changed = true;
        updatedMappings.depthMaxCol = depthMaxCol;
      }
      if (changed) {
        const updatedMetadata = {
          ...metadata,
          dataset: updatedMappings,
        };
        applyUpdate(updatedMetadata);
      }
    }
  }, [bhidCol, depthCol, depthMinCol, depthMaxCol]);

  return {
    metadata,
    getMetadata,
    updateMetadata: applyUpdate,
    alterSignalsMap,
    unloadMetadata,
  };
};
